/**
 * Version control primitives for Automerge documents.
 *
 * Checkpoints are created via `Automerge.emptyChange()` with a JSON message
 * containing `{ type: "checkpoint", ... }`. The empty change advances the heads
 * without modifying document content, acting as a structural tombstone in the DAG.
 */

import * as Automerge from "@automerge/automerge";
import { applyPatches } from "@automerge/automerge";
import {
  type AutomergeUrl,
  type DocHandle,
  parseAutomergeUrl,
  type Repo,
  type UrlHeads,
} from "@automerge/automerge-repo";
import { invariant } from "ts-invariant";

import {
  changeSlot,
  createDirectoryCache,
  createDirectoryTree,
  listedEntries,
  plainCreateDoc,
  type CreateDoc,
} from "./fs/dir-tree";
import { evictDoc } from "./fs/doc-eviction";
import { basename, ensureParentDirs, resolveInode } from "./fs/paths";
import type { DirectoryDoc, PlayFileInode } from "./fs/types";
import {
  cloneCheckpointContent,
  type IndexCloneDoc,
  isIndexCloneDoc,
  withoutVersion,
  withoutVersions,
} from "./history/clone";
import { normalizeDirPath, walkScope } from "./history/scope";
import {
  isLinkedProject,
  type CheckpointContent,
  type CheckpointRef,
  type Project,
  type ProjectCheckpoint,
} from "./project";

export interface CheckpointMeta {
  type: "checkpoint";
  label?: string;
  author?: string;
  timestamp?: number;
}

/** Parse a change message as checkpoint metadata. Returns null if it isn't one. */
export function parseCheckpointMessage(
  message: string | undefined,
): CheckpointMeta | null {
  if (!message) return null;
  try {
    const parsed = JSON.parse(message);
    if (parsed?.type === "checkpoint") return parsed as CheckpointMeta;
    return null;
  } catch {
    return null;
  }
}

/** Returns true if `message` is a checkpoint marker. */
export function isCheckpoint(message: string | undefined): boolean {
  return parseCheckpointMessage(message) !== null;
}

/**
 * Walk history backward to find the heads at the last checkpoint.
 * Returns null if no checkpoint exists.
 */
export function findLastCheckpointHeads<T>(
  handle: DocHandle<T>,
): UrlHeads | null {
  const history = handle.history();
  if (!history) return null;

  for (let i = history.length - 1; i >= 0; i--) {
    const heads = history[i];
    invariant(heads, "Loop over history somehow resulted in undefined heads");
    const primaryHead = heads[0];
    if (!primaryHead) continue;
    const meta = handle.metadata(primaryHead);
    if (isCheckpoint(meta?.message || undefined)) {
      return heads;
    }
  }
  return null;
}

/** Returns true if the document has been modified since its last checkpoint. */
export function isModifiedSinceCheckpoint<T>(handle: DocHandle<T>): boolean {
  const lastCheckpoint = findLastCheckpointHeads(handle);
  const currentHeads = handle.heads()?.join(",") ?? "";
  if (!lastCheckpoint) {
    // No checkpoint exists — dirty if doc has any history
    const history = handle.history();
    return !!history && history.length > 0;
  }
  return currentHeads !== lastCheckpoint.join(",");
}

/** Create a checkpoint marker on a document (emptyChange with checkpoint metadata). */
export function checkpoint<T>(handle: DocHandle<T>, label: string): void {
  handle.update((doc) =>
    Automerge.emptyChange(doc, {
      message: JSON.stringify({ type: "checkpoint", label }),
    }),
  );
}

export interface CheckpointedFile {
  file: string;
  heads: string[];
}

export interface CheckpointProjectResult {
  /** Files that were checkpointed with their new heads. */
  checkpointed: CheckpointedFile[];
  /** Current heads for every Automerge-backed file, keyed by filename. */
  heads: Record<string, string[]>;
}

/**
 * Checkpoint all modified Automerge documents in a project.
 *
 * Iterates every file in the project index, checkpoints any that have been
 * modified since their last checkpoint, and returns the resulting heads so
 * consumers (e.g. the client) can wait for them to sync.
 */
export async function checkpointProject(
  repo: Repo,
  files: Record<string, PlayFileInode>,
  label: string,
): Promise<CheckpointProjectResult> {
  const checkpointed: CheckpointedFile[] = [];
  const heads: Record<string, string[]> = {};

  const { files: entries } = await walkScope(repo, files, "/");
  for (const { path: filename, inode } of entries) {
    if (!inode.uri.startsWith("automerge:")) continue;
    if (inode.version?.kind === "automerge") {
      heads[filename] = [...inode.version.heads];
      continue;
    }

    try {
      const handle = await repo.find(inode.uri as AutomergeUrl);
      await handle.whenReady();

      if (isModifiedSinceCheckpoint(handle)) {
        checkpoint(handle, label);
        const fileHeads = handle.heads() ?? [];
        checkpointed.push({ file: filename, heads: fileHeads });
      }
      heads[filename] = handle.heads() ?? [];
    } catch (error) {
      console.error(`Failed to checkpoint ${filename}:`, error);
    }
  }

  return { checkpointed, heads };
}

/**
 * Collect the current heads of every Automerge-backed file in a project,
 * keyed by Automerge URL. Keying by URL (not filename) keeps the snapshot
 * valid across renames — the doc identity is the URL, not the path.
 * Non-Automerge files (S3 URIs, etc.) are skipped since they don't have
 * head-based version history.
 */
export async function collectCurrentHeads(
  repo: Repo,
  files: Record<string, PlayFileInode>,
): Promise<Record<string, string[]>> {
  const heads: Record<string, string[]> = {};
  for (const { inode } of (await walkScope(repo, files, "/")).files) {
    if (!inode.uri.startsWith("automerge:")) continue;
    if (heads[inode.uri]) continue;
    const handle = await repo.find(inode.uri as AutomergeUrl);
    await handle.whenReady();
    heads[inode.uri] = [...(handle.heads() ?? [])];
  }
  return heads;
}

export function generateCheckpointId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `cp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// ---------------------------------------------------------------------------
// Scoped (directory) checkpoints
//
// A checkpoint always targets a directory `path` — `"/"` is the whole project
// (the root directory). Capturing is recursive over the subtree: it records the
// content heads of every descendant file plus the heads of the project index
// doc (the structure source of truth). Restoring makes the subtree byte-for-byte
// identical to the captured state — content AND structure — independent of edits
// elsewhere in the project.
// ---------------------------------------------------------------------------

/**
 * Map a directory path to the Automerge path prefix of its location within the
 * project index doc. Root entries live at `files[name]`; nested entries at
 * `inode.files.entries[child]`. So `"a/b"` →
 * `["files","a","files","entries","b"]` and `"/"` → `["files"]`.
 */
export function indexDocPathForDir(
  path: string | undefined,
): (string | number)[] {
  const norm = normalizeDirPath(path);
  if (norm === "/") return ["files"];
  const segments = norm.split("/");
  const prefix: (string | number)[] = ["files"];
  segments.forEach((seg, i) => {
    if (i === 0) prefix.push(seg);
    else prefix.push("files", "entries", seg);
  });
  return prefix;
}

/** Deep-clone an inode subtree to plain JS so it can be re-assigned into an
 * Automerge `change()`. Inodes are JSON-safe (strings, numbers, nested maps). */
function clonePlain<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * Read the current heads of each doc, keyed by url. A checkpoint IS this map,
 * and answering it means hydrating every doc in scope — so where the walk runs
 * is the whole cost. A storageless peer passes a reader aimed at the plane
 * already holding the docs rather than pulling each one across the wire.
 *
 * Total in the urls it is given: a url no source has is absent from the result
 * rather than a rejection, since restore already skips a url with no heads. So
 * a doc the index outlived costs that file its undo point, not the capture.
 */
export type HeadsReader = (
  urls: AutomergeUrl[],
) => Promise<Record<string, string[]>>;

/** Hydrate, read, put back whatever wasn't already resident. One at a time:
 *  the width of this walk is a memory bound, and a whole-project capture
 *  otherwise peaks at the entire workspace. Inline eviction is safe because
 *  these loads are read-only (evictDoc's CAUTION, context b). */
export function readHeadsViaRepo(repo: Repo): HeadsReader {
  return async (urls) => {
    const heads: Record<string, string[]> = {};
    for (const url of urls) {
      const documentId = parseAutomergeUrl(url).documentId;
      const wasResident = repo.handles[documentId] !== undefined;
      // Absence is read off the query's own state, not a rejection message.
      const query = repo.findWithProgress(url);
      try {
        heads[url] = [...((await query.whenReady()).heads() ?? [])];
      } catch (error) {
        if (query.peek().state !== "unavailable") throw error;
      }
      if (!wasResident) await evictDoc(repo, documentId);
    }
    return heads;
  };
}

/**
 * Answer from this repo for the docs it holds, delegate the rest.
 *
 * A capture's `structure` is this peer's, so its heads must be too wherever
 * this peer has an opinion. A resident handle carries writes that may not have
 * reached a sync peer yet (Automerge acks nothing, and a side-channel read
 * doesn't queue behind the frame), so asking the far side for those pairs a
 * post-write structure with pre-write heads and drops the edit.
 */
export function readHeadsResidentFirst(
  repo: Repo,
  remote: HeadsReader,
): HeadsReader {
  return async (urls) => {
    const local: Record<string, string[]> = {};
    const delegated: AutomergeUrl[] = [];
    for (const url of urls) {
      // A loading handle can't hold a local write, so it delegates.
      const handle = repo.handles[parseAutomergeUrl(url).documentId];
      if (handle?.state === "ready") local[url] = [...(handle.heads() ?? [])];
      else delegated.push(url);
    }
    return { ...(await remote(delegated)), ...local };
  };
}

/** A capture's phases, in the order they run. */
export type CapturePhase =
  | "walk"
  | "heads"
  | "structure"
  | "persist"
  | "commit";

/** Counts and sizes a phase reports about its own work. Never ids. */
export type PhaseFacts = Record<string, number>;

/**
 * Runs one phase of a capture. A host that times captures (spans, a log line)
 * wraps `fn`; `note` records facts as they become known, so a phase that
 * throws or hangs has still said how much it was asked to do.
 */
export type PhaseRunner = <T>(
  phase: CapturePhase,
  fn: (note: (facts: PhaseFacts) => void) => Promise<T>,
) => Promise<T>;

/** The default {@link PhaseRunner}: runs the phase and records nothing. */
export const untimedPhase: PhaseRunner = (_phase, fn) => fn(() => {});

/**
 * Build the immutable {@link CheckpointContent} payload for the subtree at
 * `path` (`"/"` = whole project): the content heads of every in-scope leaf doc
 * plus the captured file-tree `structure` (self-contained, so restore never has
 * to view the project doc at historical heads — the SUP-191 "strategic unlock").
 *
 * Pure capture: reads the world (and evicts docs it cold-loaded), creates no
 * docs and mutates nothing. `checkpoints.ts:createCheckpoint` turns the result
 * into an immutable content doc + a refs entry.
 */
export async function captureCheckpointContent(
  repo: Repo,
  files: Record<string, PlayFileInode>,
  path: string,
  readHeads: HeadsReader = readHeadsViaRepo(repo),
  phase: PhaseRunner = untimedPhase,
): Promise<CheckpointContent> {
  const dir = normalizeDirPath(path);
  const { walk, urls } = await phase("walk", async (note) => {
    const walk = await walkScope(repo, files, dir);
    const urls = [
      ...new Set(
        walk.files
          .map(({ inode }) => inode.uri)
          .filter((uri) => uri.startsWith("automerge:")),
      ),
    ] as AutomergeUrl[];
    note({
      "file.count": walk.files.length,
      "doc.count": urls.length,
      "dir.count": Object.keys(walk.directories).length,
    });
    return { walk, urls };
  });
  const docHeads = await phase("heads", async (note) => {
    // `repo.handles` rebuilds a map on every read: take it once.
    const handles = repo.handles;
    const resident = urls.filter(
      (url) => handles[parseAutomergeUrl(url).documentId]?.state === "ready",
    ).length;
    note({ "docs.requested": urls.length, "docs.resident": resident });
    const read = await readHeads(urls);
    note({ "docs.missing": urls.length - Object.keys(read).length });
    return read;
  });
  const heads = { ...docHeads, ...walk.directories };
  const directories = Object.keys(walk.directories);
  // Clone the captured subtree to plain JS so it can be embedded in the content
  // doc and later re-assigned inside an Automerge `change()`. Root scope stores
  // the whole `files` map; a subtree stores its single dir inode (or `null` if
  // that directory doesn't exist: degenerate, but keeps the type total). A
  // linked directory's entries are its own document's, versioned in `heads`.
  const structure: CheckpointContent["structure"] = await phase(
    "structure",
    async () =>
      dir === "/" ? clonePlain(files) : clonePlain(walk.scope ?? null),
  );
  return {
    path: dir,
    heads,
    structure,
    ...(directories.length > 0 && { directories }),
  };
}

/**
 * Restore a single Automerge doc's content to `targetHeads` by diffing the
 * current state against the target and applying the resulting patches as a new
 * change. The original changes aren't deleted — Automerge can't rewrite history
 * — but the doc's logical state is reset to the snapshot. Returns true if any
 * patch was applied.
 */
export async function restoreDocToHeads(
  repo: Repo,
  url: AutomergeUrl,
  targetHeads: UrlHeads,
  message: string,
): Promise<boolean> {
  const handle = await repo.find(url);
  await handle.whenReady();
  const patches = handle.diff(targetHeads);
  if (patches.length === 0) return false;
  handle.change(
    (doc: unknown) => {
      applyPatches(doc as Record<string, unknown>, patches);
    },
    { message },
  );
  return true;
}

/**
 * Reset a linked directory's entries to what they were at `heads`: names added
 * since are removed, removed ones relinked, changed ones rewritten. Conflicts
 * captured there come back as their aliases, as real entries.
 */
async function resetDirectory(
  repo: Repo,
  url: AutomergeUrl,
  heads: UrlHeads,
  message: string,
): Promise<boolean> {
  const handle = await repo.find<DirectoryDoc>(url);
  await handle.whenReady();
  const past = handle.view(heads).doc()?.entries;
  invariant(past, `directory ${url} has no entries at the captured heads`);
  const target = withoutVersions(listedEntries(past));
  const live = handle.doc()?.entries ?? {};
  const same = (name: string) =>
    JSON.stringify(live[name]) === JSON.stringify(target[name]);
  const names = new Set([...Object.keys(live), ...Object.keys(target)]);
  if ([...names].every(same)) return false;
  handle.change(
    (doc) => {
      for (const name of names) {
        if (!(name in target)) delete doc.entries[name];
        else if (!same(name)) doc.entries[name] = target[name]!;
      }
    },
    { message },
  );
  return true;
}

export interface RestoreCheckpointResult {
  /** URLs of leaf docs whose content was restored. */
  restored: string[];
  /** URLs skipped (non-automerge, empty heads, or unloadable). */
  skipped: string[];
}

/**
 * Resolve a checkpoint to its {@link CheckpointContent}, whichever storage form
 * it's in. This is THE adapter: every reader of a checkpoint (restore, the
 * history views, `history/read.ts`) comes through here, so a new storage
 * format is one more arm and no consumer changes.
 *  - a SUP-314 index clone (`kind: "index-clone"`) → project the clone's
 *    scoped file tree and per-inode versions onto this shape. Structure
 *    inodes keep their `version`; `restoreStructure` strips it on the way
 *    back into the live index;
 *  - a SUP-191 {@link CheckpointRef} → load its immutable content doc;
 *  - a legacy {@link ProjectCheckpoint} → synthesise the content, reconstructing
 *    `structure` from `indexHeads` (view the project doc at those heads) exactly
 *    as the old restore did. If `indexHeads` is missing/unusable the structure
 *    is `null` → restore falls back to content-only, matching legacy behaviour.
 */
export async function loadCheckpointContent(
  repo: Repo,
  indexUrl: AutomergeUrl,
  checkpoint: CheckpointRef | ProjectCheckpoint,
): Promise<CheckpointContent> {
  if ("contentUrl" in checkpoint && checkpoint.kind === "index-clone") {
    const handle = await repo.find<IndexCloneDoc>(checkpoint.contentUrl);
    await handle.whenReady();
    const doc = handle.doc();
    invariant(
      isIndexCloneDoc(doc),
      `checkpoint ${checkpoint.id} is not a completed index clone: ${checkpoint.contentUrl}`,
    );
    return cloneCheckpointContent(doc);
  }
  if ("contentUrl" in checkpoint) {
    const handle = await repo.find<CheckpointContent>(checkpoint.contentUrl);
    await handle.whenReady();
    const doc = handle.doc();
    invariant(
      doc,
      `checkpoint content doc unavailable: ${checkpoint.contentUrl}`,
    );
    return {
      path: doc.path,
      heads: doc.heads,
      structure: doc.structure ?? null,
      ...(doc.directories && { directories: [...doc.directories] }),
    };
  }
  const dir = normalizeDirPath(checkpoint.path);
  let structure: CheckpointContent["structure"] = null;
  if (checkpoint.indexHeads && checkpoint.indexHeads.length > 0) {
    try {
      const indexHandle = await repo.find<{
        files: Record<string, PlayFileInode>;
      }>(indexUrl);
      await indexHandle.whenReady();
      const pastFiles =
        indexHandle.view(checkpoint.indexHeads as UrlHeads).doc()?.files ?? {};
      if (dir === "/") {
        structure = pastFiles;
      } else {
        structure = resolveInode(pastFiles, dir) ?? null;
      }
    } catch {
      // Project doc re-seeded (or heads otherwise unresolvable): fall back to
      // content-only restore rather than aborting.
      structure = null;
    }
  }
  return { path: dir, heads: checkpoint.heads, structure };
}

/**
 * Convert a legacy {@link ProjectCheckpoint} into the SUP-191
 * {@link CheckpointContent}, reconstructing `structure` from its `indexHeads`
 * (or `null` if unusable). Used by the checkpoint migration.
 */
export async function legacyCheckpointToContent(
  repo: Repo,
  indexUrl: AutomergeUrl,
  checkpoint: ProjectCheckpoint,
): Promise<CheckpointContent> {
  return loadCheckpointContent(repo, indexUrl, checkpoint);
}

/** Restore the captured file-tree `structure` into the project index doc. */
async function restoreStructure(
  repo: Repo,
  indexUrl: AutomergeUrl,
  content: CheckpointContent,
  message: string,
  createDoc: CreateDoc,
): Promise<void> {
  // `null` structure = content-only restore (legacy checkpoints without usable
  // structure). Matches the old `indexHeads`-empty guard.
  if (content.structure === null) return;
  const dir = normalizeDirPath(content.path);
  const indexHandle = await repo.find<Project>(indexUrl);
  await indexHandle.whenReady();
  const project = indexHandle.doc();
  invariant(project, `project index not loaded: ${indexUrl}`);

  // A linked tree's scope sits in its parent directory's document: relink it
  // there (parents made as the HAL makes them). Its own entries are reset with
  // the other captured directories.
  if (dir !== "/" && isLinkedProject(project)) {
    const segs = dir.split("/");
    const name = segs.pop();
    invariant(name, `no scope name in ${dir}`);
    const tree = createDirectoryTree({
      rootHandle: indexHandle,
      dirs: createDirectoryCache(repo),
      createDoc,
    });
    const parent = await tree.locate(segs, true);
    const snapshot = withoutVersion(content.structure as PlayFileInode);
    changeSlot(parent, name, (entries) => {
      entries[name] = snapshot;
    });
    return;
  }

  // Overwrite the subtree wholesale inside a `change()`. A change callback can
  // delete map keys (which `applyPatches` cannot) so files added since the
  // checkpoint are dropped and files deleted since are recreated — making the
  // subtree exact. The tree written back is live, so it carries no `version`
  // (an index clone's structure does); `withoutVersion(s)` is also the plain
  // copy a `change()` needs.
  if (dir === "/") {
    const snapshot = withoutVersions(
      content.structure as Record<string, PlayFileInode>,
    );
    indexHandle.change(
      (doc) => {
        doc.files = snapshot;
      },
      { message },
    );
  } else {
    const snapshot = withoutVersion(content.structure as PlayFileInode);
    indexHandle.change(
      (doc) => {
        // Recreate any missing parent directories (mkdir -p) before writing the
        // snapshot, so a nested restore still lands even if an ancestor was
        // deleted since the checkpoint. Without this the leaf docs would
        // restore but stay unreachable from the tree.
        const parentEntries = ensureParentDirs(doc.files, dir);
        parentEntries[basename(dir)] = snapshot;
      },
      { message },
    );
  }
}

/**
 * Restore the directory subtree captured by `checkpoint` to its exact state:
 *
 *  1. Structure — overwrite the subtree in the project index doc with the
 *     captured `structure`, re-creating files deleted since, removing files
 *     added since, and undoing renames *within* the subtree.
 *  2. Content — reset each in-scope leaf doc to its captured heads.
 *
 * Accepts either a SUP-191 {@link CheckpointRef} (loads the immutable content
 * doc) or a legacy {@link ProjectCheckpoint} (inline heads + `indexHeads`).
 *
 * Docs are addressed by URL, so restore survives renames. Files deleted after
 * the checkpoint restore because their backing Automerge doc still exists in
 * the repo. (Renaming the scoped directory *itself* is the one boundary case
 * not fully reverted — operations *within* the directory are exact.)
 */
export async function restoreCheckpoint(
  repo: Repo,
  indexUrl: AutomergeUrl,
  checkpoint: CheckpointRef | ProjectCheckpoint,
  message: string,
  createDoc: CreateDoc = plainCreateDoc(repo),
): Promise<RestoreCheckpointResult> {
  const content = await loadCheckpointContent(repo, indexUrl, checkpoint);

  // 1. Structure.
  await restoreStructure(repo, indexUrl, content, message, createDoc);

  // 2. Content: reset each captured leaf doc, and each linked directory's
  //    entries (a directory is a map whose keys come and go, which patches
  //    can't delete).
  const directories = new Set(content.directories ?? []);
  const restored: string[] = [];
  const skipped: string[] = [];
  for (const [url, target] of Object.entries(content.heads)) {
    if (!url.startsWith("automerge:") || !target || target.length === 0) {
      skipped.push(url);
      continue;
    }
    try {
      const isDirectory = directories.has(url);
      const reset = isDirectory ? resetDirectory : restoreDocToHeads;
      await reset(repo, url as AutomergeUrl, target as UrlHeads, message);
      // `restored` counts files; a directory is their structure.
      if (!isDirectory) restored.push(url);
    } catch (error) {
      console.error(`Failed to restore ${url}:`, error);
      skipped.push(url);
    }
  }
  return { restored, skipped };
}

// ---------------------------------------------------------------------------
// Single-document change history projection
// ---------------------------------------------------------------------------

export interface DocChange {
  /** Heads after this change. */
  heads: UrlHeads;
  /** Heads before this change (undefined for the first change). */
  prevHeads?: UrlHeads;
  /** Raw Automerge change message. */
  message?: string;
  /** Author parsed from `message` if it was JSON-encoded. */
  author?: string;
  /** Whether this change came from a human, an agent, or unknown. */
  authorKind: "user" | "agent" | "unknown";
  /** Automerge actor id. */
  actorId?: string;
  /** Unix seconds when the change was committed. */
  time?: number;
  /** Sequence number within the doc. */
  seq?: number;
  /** Change hash (first 8 are usually plenty for display). */
  hash?: string;
}

function authorKindOf(author: string): DocChange["authorKind"] {
  if (author.startsWith("agent:")) return "agent";
  if (author.startsWith("user:")) return "user";
  return "unknown";
}

/** Parse `{ author }` out of a JSON change message. */
function parseAuthorFromMessage(message?: string | null): {
  author?: string;
  authorKind: DocChange["authorKind"];
} {
  if (!message) return { authorKind: "unknown" };
  try {
    const parsed = JSON.parse(message) as { author?: unknown };
    if (typeof parsed.author === "string") {
      return { author: parsed.author, authorKind: authorKindOf(parsed.author) };
    }
  } catch {
    // not JSON — treat as plain text
  }
  return { authorKind: "unknown" };
}

/**
 * Project a single doc's full change history in chronological order (oldest
 * first). Each entry pairs the heads *after* a change with the heads *before*
 * it, so a consumer can restore to any point via {@link restoreDocToHeads}.
 */
export function projectDocHistory<T>(handle: DocHandle<T>): DocChange[] {
  const history = handle.history() ?? [];
  const changes: DocChange[] = [];
  for (let i = 0; i < history.length; i++) {
    const heads = history[i];
    if (!heads) continue;
    const primary = heads[0];
    const meta = primary ? handle.metadata(primary) : undefined;
    const { author, authorKind } = parseAuthorFromMessage(meta?.message);
    changes.push({
      heads,
      prevHeads: i > 0 ? history[i - 1] : undefined,
      message: meta?.message ?? undefined,
      author,
      authorKind,
      actorId: meta?.actor,
      time: meta?.time,
      seq: meta?.seq,
      hash: meta?.hash,
    });
  }
  return changes;
}
